import type { LucideIcon } from "lucide-react";
import {
	ArrowDownLeft,
	ArrowLeftRight,
	ArrowUpRight,
	Ban,
	CheckCircle,
	CircleDashed,
	CircleDot,
	Clock,
	Flag,
	Lock,
	Pause,
	ShieldCheck,
	ShieldX,
	Undo2,
	XCircle,
} from "lucide-react";

export interface StatusConfig {
	icon: LucideIcon;
	color: string;
	label: string;
	badgeType: "success" | "warning" | "error" | "info" | "default" | "danger";
}

export const STATUS_CONFIG: Record<string, StatusConfig> = {
	success: {
		icon: CheckCircle,
		color: "#22c55e",
		label: "Success",
		badgeType: "success",
	},
	paid: {
		icon: CheckCircle,
		color: "#22c55e",
		label: "Paid",
		badgeType: "success",
	},
	completed: {
		icon: CheckCircle,
		color: "#22c55e",
		label: "Completed",
		badgeType: "success",
	},
	approved: {
		icon: CheckCircle,
		color: "#22c55e",
		label: "Approved",
		badgeType: "success",
	},
	resolved: {
		icon: CheckCircle,
		color: "#22c55e",
		label: "Resolved",
		badgeType: "success",
	},
	accepted: {
		icon: CheckCircle,
		color: "#22c55e",
		label: "Accepted",
		badgeType: "success",
	},
	active: {
		icon: CheckCircle,
		color: "#22c55e",
		label: "Active",
		badgeType: "success",
	},
	pending: {
		icon: Clock,
		color: "#eab308",
		label: "Pending",
		badgeType: "warning",
	},
	pending_approval: {
		icon: Clock,
		color: "#eab308",
		label: "Pending Approval",
		badgeType: "warning",
	},
	on_hold: {
		icon: Pause,
		color: "#eab308",
		label: "On Hold",
		badgeType: "warning",
	},
	onHold: {
		icon: Pause,
		color: "#eab308",
		label: "On Hold",
		badgeType: "warning",
	},
	flagged: {
		icon: Flag,
		color: "#eab308",
		label: "Flagged",
		badgeType: "warning",
	},
	open: {
		icon: Clock,
		color: "#eab308",
		label: "Open",
		badgeType: "warning",
	},
	inProgress: {
		icon: CircleDashed,
		color: "#3b82f6",
		label: "In Progress",
		badgeType: "info",
	},
	in_progress: {
		icon: CircleDashed,
		color: "#3b82f6",
		label: "In Progress",
		badgeType: "info",
	},
	processing: {
		icon: CircleDashed,
		color: "#3b82f6",
		label: "Processing",
		badgeType: "info",
	},
	confirmed: {
		icon: CheckCircle,
		color: "#3b82f6",
		label: "Confirmed",
		badgeType: "info",
	},
	acknowledged: {
		icon: CheckCircle,
		color: "#3b82f6",
		label: "Acknowledged",
		badgeType: "info",
	},
	partially_paid: {
		icon: CircleDot,
		color: "#3b82f6",
		label: "Partially Paid",
		badgeType: "info",
	},
	locked: {
		icon: Lock,
		color: "#3b82f6",
		label: "Locked",
		badgeType: "info",
	},
	failed: {
		icon: XCircle,
		color: "#ef4444",
		label: "Failed",
		badgeType: "error",
	},
	error: {
		icon: XCircle,
		color: "#ef4444",
		label: "Error",
		badgeType: "error",
	},
	rejected: {
		icon: Ban,
		color: "#ef4444",
		label: "Rejected",
		badgeType: "error",
	},
	revoked: {
		icon: XCircle,
		color: "#ef4444",
		label: "Revoked",
		badgeType: "error",
	},
	aml_rejected: {
		icon: ShieldX,
		color: "#ef4444",
		label: "AML Rejected",
		badgeType: "danger",
	},
	cancelled: {
		icon: Ban,
		color: "#6b7280",
		label: "Cancelled",
		badgeType: "default",
	},
	canceled: {
		icon: Ban,
		color: "#6b7280",
		label: "Canceled",
		badgeType: "default",
	},
	expired: {
		icon: XCircle,
		color: "#6b7280",
		label: "Expired",
		badgeType: "default",
	},
	inactive: {
		icon: Ban,
		color: "#6b7280",
		label: "Inactive",
		badgeType: "default",
	},
};

export const TRANSACTION_TYPE_CONFIG: Record<string, StatusConfig> = {
	incoming: {
		icon: ArrowDownLeft,
		color: "#3b82f6",
		label: "Incoming",
		badgeType: "info",
	},
	internal: {
		icon: ArrowLeftRight,
		color: "#8b5cf6",
		label: "Internal",
		badgeType: "info",
	},
	withdrawal: {
		icon: ArrowUpRight,
		color: "#f97316",
		label: "Withdrawal",
		badgeType: "warning",
	},
	pool_withdrawal: {
		icon: ArrowUpRight,
		color: "#f97316",
		label: "Pool Withdrawal",
		badgeType: "warning",
	},
	fund_return: {
		icon: Undo2,
		color: "#06b6d4",
		label: "Fund Return",
		badgeType: "info",
	},
};

export const SEVERITY_CONFIG: Record<string, StatusConfig> = {
	info: {
		icon: CheckCircle,
		color: "#3b82f6",
		label: "Info",
		badgeType: "info",
	},
	warn: {
		icon: Flag,
		color: "#eab308",
		label: "Warning",
		badgeType: "warning",
	},
	warning: {
		icon: Flag,
		color: "#eab308",
		label: "Warning",
		badgeType: "warning",
	},
	error: {
		icon: XCircle,
		color: "#ef4444",
		label: "Error",
		badgeType: "error",
	},
	critical: {
		icon: XCircle,
		color: "#dc2626",
		label: "Critical",
		badgeType: "error",
	},
};

export const AML_STATUS_CONFIG: Record<string, StatusConfig> = {
	pending: {
		icon: Clock,
		color: "#eab308",
		label: "Pending",
		badgeType: "warning",
	},
	approved: {
		icon: ShieldCheck,
		color: "#22c55e",
		label: "Approved",
		badgeType: "success",
	},
	flagged: {
		icon: Flag,
		color: "#eab308",
		label: "Flagged",
		badgeType: "warning",
	},
	rejected: {
		icon: ShieldX,
		color: "#ef4444",
		label: "Rejected",
		badgeType: "error",
	},
};

export const RISK_LEVEL_CONFIG: Record<string, StatusConfig> = {
	low: {
		icon: ShieldCheck,
		color: "#22c55e",
		label: "Low Risk",
		badgeType: "success",
	},
	medium: {
		icon: Flag,
		color: "#eab308",
		label: "Medium Risk",
		badgeType: "warning",
	},
	high: {
		icon: Flag,
		color: "#f97316",
		label: "High Risk",
		badgeType: "warning",
	},
	critical: {
		icon: ShieldX,
		color: "#ef4444",
		label: "Critical Risk",
		badgeType: "error",
	},
};

const DEFAULT_STATUS_CONFIG: StatusConfig = {
	icon: CircleDot,
	color: "#6b7280",
	label: "Unknown",
	badgeType: "default",
};

export const getStatusConfig = (status: string): StatusConfig => {
	const normalized = status.toLowerCase().replace(/-/g, "_");

	if (STATUS_CONFIG[status]) {
		return STATUS_CONFIG[status];
	}

	if (STATUS_CONFIG[normalized]) {
		return STATUS_CONFIG[normalized];
	}

	return DEFAULT_STATUS_CONFIG;
};

export interface StatusBadgeProps {
	type: StatusConfig["badgeType"];
	icon: LucideIcon;
	label: string;
}

// Single source of truth for rendering a status as a StatusBadge. Maps the
// camelCase/legacy aliases the API still emits onto the canonical config.
export const getStatusBadgeProps = (status: string): StatusBadgeProps => {
	const normalized = status?.toLowerCase?.() ?? "";
	const lookupKey =
		normalized === "inprogress" || normalized === "inprogressinv"
			? "inProgress"
			: status;

	const config = getStatusConfig(lookupKey);
	return { type: config.badgeType, icon: config.icon, label: config.label };
};

export const getTransactionTypeConfig = (type: string): StatusConfig => {
	const normalized = type.toLowerCase().replace(/-/g, "_");
	return (
		TRANSACTION_TYPE_CONFIG[type] ||
		TRANSACTION_TYPE_CONFIG[normalized] ||
		DEFAULT_STATUS_CONFIG
	);
};

export const getSeverityConfig = (severity: string): StatusConfig => {
	const normalized = severity.toLowerCase();
	return (
		SEVERITY_CONFIG[severity] ||
		SEVERITY_CONFIG[normalized] ||
		DEFAULT_STATUS_CONFIG
	);
};

export const getAMLStatusConfig = (status: string): StatusConfig => {
	const normalized = status.toLowerCase();
	return (
		AML_STATUS_CONFIG[status] ||
		AML_STATUS_CONFIG[normalized] ||
		DEFAULT_STATUS_CONFIG
	);
};

export const getRiskLevelConfig = (level: string): StatusConfig => {
	const normalized = level.toLowerCase();
	return (
		RISK_LEVEL_CONFIG[level] ||
		RISK_LEVEL_CONFIG[normalized] ||
		DEFAULT_STATUS_CONFIG
	);
};

export const getFilterOptionsData = (config: Record<string, StatusConfig>) => {
	return Object.entries(config).map(([value, cfg]) => ({
		value,
		label: cfg.label,
		icon: cfg.icon,
		color: cfg.color,
	}));
};
